import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { validateEmail } from '@modules/common/utils';
import { getCollaboratorInitials } from '@modules/dashboard/helpers';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DashboardCollaborator } from '@modules/dashboard/types';
import { Autocomplete, Avatar, Box, Chip, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useListUsers } from 'v2/core/services/listUsers';
import { useGetUser } from 'v2/core/services/user';

const INVALID_EMAILS_INFO = 'One or more emails are invalid';

interface DashboardShareByEmailDropdownProps {
  setEditorsToAdd: (editors: DashboardCollaborator[]) => void;
  editorsToAdd: DashboardCollaborator[];
}

export const DashboardShareByEmailDropdown = ({
  setEditorsToAdd,
  editorsToAdd,
}: DashboardShareByEmailDropdownProps) => {
  const [inputValue, setInputValue] = useState('');

  const { data, error, status } = useListUsers();
  const {
    id: currentUserID,
    tenant: { domains },
  } = useGetUser();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const usersList = data?.users;
  const editors = dashboard?.editors;

  const eligibleUsersList = useMemo(
    () =>
      usersList
        ?.filter(
          (user) =>
            !editors?.some((editor) => editor.id === user.id) &&
            user.id !== currentUserID,
        )
        .map(
          (user): DashboardCollaborator => ({ ...user, isExisting: true }),
        ) || [],
    [usersList, editors, currentUserID],
  );

  const isUsersLoading = IsLoading(status);

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: 'Failed to fetch users',
        props: {
          isClosable: true,
        },
      });
    }
  }, [error, showErrorNotification]);

  const onEmailSelect = (
    _: React.SyntheticEvent<Element, Event>,
    editors: (string | DashboardCollaborator)[],
  ) => {
    const editorsToAdd = editors.map((editor): DashboardCollaborator => {
      if (typeof editor !== 'string') {
        return editor;
      }
      return (
        eligibleUsersList.find(({ email }) => email === editor) ?? {
          isExisting: false,
          email: editor,
          isValidEmail: validateEmail(editor, domains).isValid,
        }
      );
    });
    setEditorsToAdd(editorsToAdd);
    setInputValue('');
  };

  const getOptionLabel = (option: DashboardCollaborator) =>
    option.isExisting
      ? [option.firstName, option.lastName].join(' ')
      : option.email;

  const hasInvalidEmails = !!editorsToAdd.find(
    (editor) => !editor.isExisting && !editor.isValidEmail,
  );

  const getChipColor = (option: DashboardCollaborator) => {
    if (!option.isExisting && !option.isValidEmail) {
      return 'error';
    }
    return undefined;
  };

  const getAvatarColor = (option: DashboardCollaborator) => {
    if (!option.isExisting && !option.isValidEmail) {
      return 'error.main';
    }
    return undefined;
  };

  return (
    <Autocomplete
      freeSolo
      multiple
      value={editorsToAdd}
      id="invite-emails"
      isOptionEqualToValue={(option, value) => option.email === value?.email}
      options={eligibleUsersList}
      loading={isUsersLoading}
      limitTags={6}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.email
      }
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Enter email IDs"
          error={hasInvalidEmails}
          helperText={hasInvalidEmails ? INVALID_EMAILS_INFO : undefined}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant={option.isExisting ? 'filled' : 'outlined'}
            label={getOptionLabel(option)}
            color={getChipColor(option)}
            avatar={
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(option),
                  color: !option.isExisting ? 'black !important' : undefined,
                }}
              >
                {getCollaboratorInitials(option)}
              </Avatar>
            }
            size="medium"
            {...getTagProps({ index })}
          />
        ))
      }
      renderOption={(props, option) => (
        <Box {...props} component="li" style={{ padding: '16px' }}>
          {option.email}
        </Box>
      )}
      onChange={onEmailSelect}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={(e) => {
        if (!inputValue.trim()) {
          setInputValue('');
          return;
        }
        onEmailSelect(e, [...editorsToAdd, inputValue]);
      }}
    />
  );
};
